import React, { useContext, useEffect, useMemo } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import RouteGuard, { RenderDashboard } from "RouteGuard";
import Join from "components/Join";
import Login from "components/auth/Login";
import Password from "components/auth/RecoverPassword";
import PricingTable from "components/PricingTable";
import ResetPass from "components/auth/ResetPassword";
import ServiceModeBanner from "components/ServiceModeBanner";
import CheckYourEmail from "components/common/CheckYourEmail";
import Confirm from "components/email/Confirm";
import RegisterBasic from "components/register/RegisterBasic";
import RegisterInvited from "components/register/RegisterInvited";
import RegisterPremierUser from "components/register/RegisterPremierUser";
import NotFound from "components/NotFound";
import allRoutes, { delegateUserRoutes, officeKioskRoutes } from "routes";
import Home from "components/Home";
import CancellationPolicy from "components/pages/CancellationPolicy";
import AccountCreation from "components/auth/ClientRegistration/AccountCreation";
import RequestDemoForm from "components/homeRedesign/RequestDemoForm";
import MyRepCares from "components/MyRepCares";
import { UserContext } from "components/context/UserContext";
import "components/client/dashboard/profile/Cards/cards.css";
import { ROLE_NAMES } from "helpers/common";
import ReferralsButton from "components/rep/referral/ReferralsButton";
import DelegateRouteGuard from "DelegateRouteGuard";
import { listSupademosForUser } from "services/supademosService";
import SupademoGlobal from "components/supademos/SupademoGlobal";

const AppRouter = () => {
  const { state, dispatch } = useContext(UserContext)!;
  const location = useLocation();
  const { currentUser } = state;

  const fetchSupaDemos = async () => {
    try {
      const resp = await listSupademosForUser({ limit: 500, offset: 0 });
      dispatch({
        type: "SET_SUPADEMOS",
        payload: {
          supademos: resp?.demos ?? [],
          lastSupademosFetchTime: Date.now(),
        },
      });
    } catch (e) {
      // keep silent; supademos are non-critical
      console.error("Failed to load supademos for user:", e);
    }
  };

  const isRep = useMemo(
    () =>
      [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER].includes(
        currentUser?.roleName,
      ),
    [currentUser],
  );

  const showFab = useMemo(() => {
    const isBannedLocation = [
      "/login",
      "/register",
      "/chat",
      "/chat/*",
      "/office-kiosk/dashboard",
    ].some((p) => location.pathname.includes(p));

    return currentUser?.id && !isBannedLocation;
  }, [currentUser, location]);
  const showSupademos = useMemo(() => {
    const isBannedLocation = [
      "/login",
      "/register",
      "/chat",
      "/chat/*",
      "/office-kiosk/dashboard",
    ].some((p) => location.pathname.includes(p));

    return currentUser?.id && !isBannedLocation;
  }, [currentUser, location]);
  useEffect(() => {
    if (!currentUser?.id) return;

    const minutesSinceLastFetch =
      (Date.now() - (state.lastSupademosFetchTime || 0)) / (1000 * 60);

    // fetch once per session (or every 10 minutes)
    if (minutesSinceLastFetch < 10 && state.supademos?.length) return;
    fetchSupaDemos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  return (
    <>
      <ServiceModeBanner />
      <Switch>
        {allRoutes.map((route, i) => (
          <RouteGuard key={route.path + i} {...route}>
            <Route {...route} />
          </RouteGuard>
        ))}

        {officeKioskRoutes.map((route, i) => (
          <RouteGuard key={route.path + i} {...route}>
            <Route {...route} />
          </RouteGuard>
        ))}

        <Route path={"/"} exact render={() => <Home />} />
        <Route path={`/password`} component={Password} />
        <Route path={`/reset/:token`} component={ResetPass} />
        <Route path={`/register`} exact component={Join} />
        <Route path={`/join-platform/:token`} component={RegisterInvited} />
        <Route path={`/rep/premier`} component={RegisterPremierUser} />
        <Route path={`/client`} exact component={AccountCreation} />
        <Route path={`/rep/basic`} component={RegisterBasic} />
        <Route path={"/check/email"} component={CheckYourEmail} />
        <Route path={`/pricing`} component={PricingTable} />
        <Route path={`/confirm/:token`} component={Confirm} />
        <Route path={`/login`} component={Login} />
        <Route path={`/request-demo`} component={RequestDemoForm} />
        <Route
          path={`/subscription-renewal-cancellation-policy`}
          component={CancellationPolicy}
        />
        {delegateUserRoutes.map((route, i) => (
          <DelegateRouteGuard key={route.path + i} {...route}>
            <Route {...route} />
          </DelegateRouteGuard>
        ))}
        {/* <Route
          path={`/impersonation-options`}
          component={ImpersonationOptions}
        />
        <Route path={`/delegate/calendar`} component={DelegateCalendar} /> */}

        <Route
          path="*"
          component={() => (
            <RenderDashboard>
              <NotFound />
            </RenderDashboard>
          )}
        />
      </Switch>

      <div className="fab-container">
        {showSupademos && <SupademoGlobal />}
        {showFab && <MyRepCares />}
        {isRep && showFab && <ReferralsButton />}
      </div>
    </>
  );
};

export default AppRouter;
